import { useRouter } from "next/router";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { Variant } from "../../types/types";
import { getSizeReductionLabelInfo } from "../../utils/product/getSizeReductionLabelInfo";
import { getSizeQuery } from "../../utils/routing/getSizeQuery";
import { StandardProps } from "../componentMap";
import { SizeGroupsUI } from "./SizeGroupsUI";
import { createSelectedSizeUrlObj } from "../Sizes/utils/createSelectedSizeUrl";
import {
  getBreadCrumbs,
  isFeatureEnabledForCategory,
} from "../FitFinder/FitFinderUtils";
import { babyAndToddlerCategories } from "./utils";
import { FitToolAndSizeChart } from "../FitToolAndSizeChart/FitToolAndSizeChart";
import { SizeAndFitCta } from "../SizeAndFitCta/SizeAndFitCta";
import { isSelected } from "../Sizes/Sizes";
import { isInStoreOnly } from "../../utils/variant/isInStoreOnly";
import { isFutureRelease } from "../../utils/variant/isFutureRelease";

export function SizeGroups({
  product,
  configProps,
  runtimeConfig,
}: Pick<StandardProps, "product" | "configProps" | "runtimeConfig">) {
  const breadCrumbs = getBreadCrumbs(product);

  const isBabyAndToddlerCategory = isFeatureEnabledForCategory(
    breadCrumbs,
    babyAndToddlerCategories,
  );

  const variants: Variant[] = product.variants;
  const { variant } = useVariant(product);
  const router = useRouter();

  const sizeParam = getSizeQuery(router.query);

  const handleClick = (newSize: string) => {
    const targetUrl = createSelectedSizeUrlObj(newSize, router.query, variant);
    router.push(targetUrl, undefined, { shallow: true, scroll: false });
  };

  const sizeVariants = variants.filter((v) => {
    if (variant.differentiators?.colour) {
      return (
        v.differentiators?.size &&
        v.differentiators?.colour === variant.differentiators.colour
      );
    } else {
      return v.differentiators?.size;
    }
  });

  if (sizeVariants.length === 0) return null;

  const sizeGroups = sizeVariants.map((sizeVariant) => {
    const { showReduced } = getSizeReductionLabelInfo(product, sizeVariant);

    return {
      size: sizeVariant.differentiators
        ? sizeVariant.differentiators?.size
          ? sizeVariant.differentiators?.size
          : ""
        : "",
      sizeHeadline: sizeVariant.differentiators?.sizeHeadline || "",
      showReduced: showReduced,
      isAvailable:
        sizeVariant.availability?.availableToOrder ||
        isInStoreOnly(sizeVariant) ||
        isFutureRelease(sizeVariant),
      isSizeReducedToClear: showReduced,
      isSelected: isSelected(sizeVariant, sizeParam, sizeVariants.length),
    };
  });

  const isSizeSelected = sizeGroups.some((size) => size.isSelected);
  const currentSize = isSizeSelected
    ? sizeGroups.filter((size) => size.isSelected).at(0)?.size
    : "";

  return (
    <div>
      {configProps?.withFitToolAndSizeChart ? (
        <FitToolAndSizeChart product={product} runtimeConfig={runtimeConfig} />
      ) : null}
      <SizeGroupsUI
        showMore={8}
        isBabyAndToddlerCategory={isBabyAndToddlerCategory}
        sizes={sizeGroups}
        currentSize={currentSize || ""}
        isSizeSelected={isSizeSelected}
        onClick={handleClick}
      />
      {configProps?.withSizeAndFitCTA ? (
        <SizeAndFitCta product={product} />
      ) : null}
    </div>
  );
}
